import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Edit, Eye, Plus, Search, Trash2 } from "lucide-react";
import blackTshirt from "../../../images/shop/black-tshirt.png";
import chainBag from "../../../images/shop/chain-bag.png";
import sneakers from "../../../images/shop/sneakers.png";
import bucketHat from "../../../images/shop/bucket-hat.png";
import backpack from "../../../images/shop/backpack.png";
import crossbodyBag from "../../../images/shop/crossbody-bag.png";

const Page = styled.main`
  color: #172033;
`;

const Header = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 18px;
  padding: 24px 26px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #eef6ff 100%);
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 30px;
`;

const Subtitle = styled.p`
  margin: 0;
  color: #66758f;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  border: 0;
  border-radius: 7px;
  padding: 0 16px;
  background: #2f80ed;
  color: #fff;
  font-weight: 900;
  cursor: pointer;
`;

const Toolbar = styled.section`
  display: grid;
  grid-template-columns: 1fr 150px 150px;
  gap: 10px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #e4edf8;
  border-radius: 8px;
  background: #fff;
`;

const SearchBox = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  height: 42px;
  padding: 0 13px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  color: #8da0b8;

  input {
    width: 100%;
    border: 0;
    outline: 0;
  }
`;

const Select = styled.select`
  height: 42px;
  border: 1px solid #dce6f2;
  border-radius: 7px;
  background: #fff;
  color: #35445c;
  padding: 0 12px;
  font-weight: 800;
`;

const Grid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
`;

const Card = styled.article`
  border: 1px solid #dfe8f4;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(32, 72, 122, 0.06);
`;

const ImageBox = styled.div`
  height: 160px;
  padding: 14px;
  background: #fff;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const Body = styled.div`
  padding: 14px;

  span {
    color: #2f80ed;
    font-size: 12px;
    font-weight: 900;
  }

  h2 {
    min-height: 42px;
    margin: 6px 0 10px;
    font-size: 16px;
    line-height: 1.35;
  }

  b {
    display: block;
    color: #172033;
    font-size: 17px;
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 12px 0;
  color: #66758f;
  font-size: 13px;
`;

const Actions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 7px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    height: 34px;
    border: 1px solid #dce6f2;
    border-radius: 7px;
    background: #fff;
    color: #66758f;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
  }
`;

const products = [
  { id: 1, brand: "GUCCI", name: "로고 크루넥 코튼 티셔츠", category: "의류", price: 890000, stock: 25, status: "판매중", image: blackTshirt },
  { id: 2, brand: "GUCCI", name: "GG 마틀라세 숄더백", category: "가방", price: 2350000, stock: 8, status: "판매중", image: chainBag },
  { id: 3, brand: "NIKE", name: "에어 조던 1 로우 블랙 화이트", category: "신발", price: 129000, stock: 43, status: "판매중", image: sneakers },
  { id: 4, brand: "PRADA", name: "리나일론 버킷햇", category: "액세서리", price: 680000, stock: 6, status: "품절임박", image: bucketHat },
  { id: 5, brand: "BURBERRY", name: "체크 백팩", category: "가방", price: 1650000, stock: 0, status: "품절", image: backpack },
  { id: 6, brand: "MAISON MARGIELA", name: "5AC 미니백", category: "가방", price: 1290000, stock: 12, status: "판매중", image: crossbodyBag },
];

export default function AdminProducts() {
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("전체");
  const [status, setStatus] = useState("전체");

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const target = `${product.brand} ${product.name} ${product.category}`.toLowerCase();
      return (
        (!keyword || target.includes(keyword.toLowerCase())) &&
        (category === "전체" || product.category === category) &&
        (status === "전체" || product.status === status)
      );
    });
  }, [category, keyword, status]);

  return (
    <Page>
      <Header>
        <div>
          <Title>상품 관리</Title>
          <Subtitle>상품 등록, 가격, 노출 상태, 재고를 한 곳에서 관리합니다.</Subtitle>
        </div>
        <PrimaryButton type="button"><Plus size={17} /> 상품 등록</PrimaryButton>
      </Header>

      <Toolbar>
        <SearchBox><Search size={17} /><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="상품명, 브랜드 검색" /></SearchBox>
        <Select value={category} onChange={(event) => setCategory(event.target.value)}>
          <option>전체</option><option>의류</option><option>가방</option><option>신발</option><option>액세서리</option>
        </Select>
        <Select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option>전체</option><option>판매중</option><option>품절임박</option><option>품절</option>
        </Select>
      </Toolbar>

      <Grid>
        {filteredProducts.map((product) => (
          <Card key={product.id}>
            <ImageBox><img src={product.image} alt={product.name} /></ImageBox>
            <Body>
              <span>{product.brand} · {product.category}</span>
              <h2>{product.name}</h2>
              <b>₩ {product.price.toLocaleString("ko-KR")}</b>
              <Meta><span>재고 {product.stock}개</span><span>{product.status}</span></Meta>
              <Actions>
                <button type="button"><Eye size={14} /> 보기</button>
                <button type="button"><Edit size={14} /> 수정</button>
                <button type="button"><Trash2 size={14} /> 삭제</button>
              </Actions>
            </Body>
          </Card>
        ))}
      </Grid>
    </Page>
  );
}
